// =============================================================================
// src/core/ui.ts — Helpers de UI compartilhados pelas secoes + fabrica de overlays.
//
// Proveniencia: assets/unified-app.js `function makeOverlay` (linhas 810-819) e
//   `var UI = {...}` (linhas 821-856). Portado FIEL: classes/markup/cores
//   (#185FA5, borderBottom 2px, fillOpacity, etc.) sao paridade visual e NAO
//   devem ser "limpas". Nomes greppaveis: `mountOverlay`/`bindOnce`/`setActiveItem`
//   no lugar do objeto generico `UI`, com um agregado `OverlayUiHelpers` que
//   satisfaz o contrato UiHelper (src/app/types.ts secao 18).
//
// SRP: so marcacao/DOM de UI reutilizavel. Sem dados, sem mapa, sem timers.
// =============================================================================

import type { UiHelper } from '../app/types';
import { qsa } from '../app/dom';

/** Id do container fixo de overlays no shell (#mapOverlays em index.html). */
const MAP_OVERLAYS_ID = 'mapOverlays';

/** Cor de destaque das abas inline (borderBottom/cor ativa) — paridade com o legado. */
const INLINE_TAB_ACTIVE_COLOR = '#185FA5';

/**
 * Cria um elemento de overlay e o anexa a #mapOverlays (replica makeOverlay).
 * Se o container nao existir, devolve o elemento criado mesmo assim (igual ao
 * original, que so anexa quando #mapOverlays esta presente).
 *
 * @example
 *   const legend = mountOverlay('<div class="u-legend">...</div>', 'u-legend');
 */
export function mountOverlay(html: string, id?: string, extraClass?: string): HTMLDivElement {
  const overlayEl = document.createElement('div');
  overlayEl.id = id ?? '';
  if (extraClass) overlayEl.className = extraClass;
  overlayEl.innerHTML = html;
  const container = document.getElementById(MAP_OVERLAYS_ID);
  if (container) container.appendChild(overlayEl);
  return overlayEl;
}

/**
 * Liga um listener UMA vez por (evento, chave) no elemento, usando uma flag no
 * proprio nó (replica UI.bindOnce). Idempotente: chamadas repetidas com a mesma
 * chave nao re-vinculam — essencial porque `activate` roda a cada scroll-spy.
 *
 * @example
 *   bindOnce(dot, 'click', 'nav-dot', () => goToSection(dot));
 */
export function bindOnce(
  element: HTMLElement | null,
  eventName: string,
  key: string,
  handler: EventListener,
): void {
  if (!element) return;
  const flag = `__bound_${eventName}_${key}`;
  const flagged = element as HTMLElement & Record<string, boolean>;
  if (flagged[flag]) return;
  flagged[flag] = true;
  element.addEventListener(eventName, handler);
}

/** Marca um item como `.active` e desmarca os demais (replica UI.setActive). */
export function setActiveItem(items: readonly Element[], activeItem: Element): void {
  items.forEach((item) => item.classList.toggle('active', item === activeItem));
}

/** Marca um item como `.selected` e desmarca os demais (replica UI.setSelected). */
export function setSelectedItem(items: readonly Element[], activeItem: Element): void {
  items.forEach((item) => item.classList.toggle('selected', item === activeItem));
}

/** Aplica o estilo inline de aba ativa a um item (extrai o corpo do forEach legado). */
function styleInlineTab(item: Element, isActive: boolean): void {
  const tab = item as HTMLElement;
  tab.classList.toggle('active', isActive);
  tab.style.borderBottom = isActive ? `2px solid ${INLINE_TAB_ACTIVE_COLOR}` : '2px solid transparent';
  tab.style.color = isActive ? INLINE_TAB_ACTIVE_COLOR : 'var(--color-text-secondary)';
  tab.style.fontWeight = isActive ? '500' : '400';
  tab.style.background = 'transparent';
}

/**
 * Sincroniza abas inline com sublinhado (replica UI.syncInlineTabs). Se `activeItem`
 * for null, mantem como ativo quem ja tiver a classe `.active` (comportamento original).
 */
export function syncInlineTabs(items: readonly Element[], activeItem?: Element | null): void {
  items.forEach((item) => {
    const isActive = activeItem ? item === activeItem : item.classList.contains('active');
    styleInlineTab(item, isActive);
  });
}

/**
 * Monta o HTML de um card de insight do Copiloto (replica UI.renderInsightCard).
 * O `onclick` inline chama `sendPrompt` (publicado no window por main.ts); o prompt
 * tem aspas simples escapadas exatamente como no original.
 */
export function renderInsightCard(title: string, text: string, prompt: string, icon?: string): string {
  const safePrompt = prompt.replace(/'/g, "\\'");
  return (
    '<div class="u-insight-card">' +
    `<div class="badge">${title}</div>` +
    `<div class="insight-text">${text}</div>` +
    `<div class="cta" onclick="sendPrompt('${safePrompt}')">` +
    `<i class="ti ${icon || 'ti-bulb'}" style="font-size:11px"></i> análise completa <i class="ti ti-arrow-up-right" style="font-size:10px" aria-hidden="true"></i></div>` +
    '</div>'
  );
}

/**
 * Agregado que satisfaz o contrato UiHelper (src/app/types.ts) para quem prefere
 * injetar o helper como objeto. Reaproveita `qsa` de app/dom (DRY).
 */
export const OverlayUiHelpers: UiHelper = {
  qsa: (selector, scope) => qsa(selector, scope ?? document),
  setActive: setActiveItem,
  setSelected: setSelectedItem,
  renderInsightCard,
};
